import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Slider } from '@mui/material';
import PlayCircleOutlineRoundedIcon from '@mui/icons-material/PlayCircleOutlineRounded';
import StopCircleRoundedIcon from '@mui/icons-material/StopCircleRounded';
import Box from '@mui/material/Box';
import { format, addMinutes, differenceInDays, parseISO } from 'date-fns';
import './slider_forecast_metric.css';

const TimeSliderForecastMetric = ({
  initialDate,
  steps,
  onChange,
  cqlFechaMin,
  issueTime,
  stepMinutes = 60 // visibility: 30, dust: 180
}) => {
  const stepsPerHour = 60 / stepMinutes;

  // 1. CALCULAR COMPORTAMIENTO (Memoizado para reactividad)
  const sliderBehavior = useMemo(() => {
    // Valores por defecto si faltan datos
    if (!cqlFechaMin || !issueTime) return {
      initialValue: 0, minValue: 0, maxValue: steps - 1, disabled: false, blockedPositions: [], dayDifference: null
    };

    try {
      const cqlDate = parseISO(cqlFechaMin);
      const issueDate = parseISO(issueTime);

      // Diferencia de días (sin horas)
      const cqlDateMidnight = new Date(cqlDate.getFullYear(), cqlDate.getMonth(), cqlDate.getDate());
      const issueDateMidnight = new Date(issueDate.getFullYear(), issueDate.getMonth(), issueDate.getDate());
      const dayDifference = differenceInDays(cqlDateMidnight, issueDateMidnight);

      // Hora de emisión (ej: si issueTime es ...07:00:00, issueHour es 7)
      const issueHour = issueDate.getHours();

      let initialValue = 0;
      let blockedPositions = [];

      switch (dayDifference) {
        case 0: // D-0: Hoy para Hoy
          // 🔥 CORRECCIÓN CLAVE: El slider inicia en la hora de emisión (ej: 7); con pasos de 3 h (dust), en la siguiente posición
          initialValue = Math.ceil(issueHour * stepsPerHour);
          // Bloqueamos las horas anteriores a la emisión
          blockedPositions = Array.from({ length: initialValue }, (_, i) => i);
          break;

        case 1: // D-1: Ayer para Hoy (Todo disponible)
          initialValue = 0;
          blockedPositions = [];
          break;

        case 2: // D-2: Hoy para Pasado Mañana (Bloqueo futuro)
          initialValue = 0;
          // Bloqueo estándar de +4 horas (ajustar según tu lógica de negocio)
          // Si cae fuera del día (dust, issue 19 → 23 h → posición 8 de 8) no queda nada que bloquear
          const blockFrom = Math.ceil((issueHour + 4) * stepsPerHour);
          blockedPositions = Array.from({ length: Math.max(0, steps - blockFrom) }, (_, i) => i + blockFrom);
          break;

        default:
          initialValue = 0;
          blockedPositions = [];
      }

      return {
        initialValue, minValue: 0, maxValue: steps - 1, disabled: false, dayDifference, issueHour, blockedPositions
      };
    } catch (error) {
      console.error('Error calculating slider behavior:', error);
      return { initialValue: 0, minValue: 0, maxValue: steps - 1, disabled: false, blockedPositions: [] };
    }
  }, [cqlFechaMin, issueTime, steps, stepsPerHour]);

  // 2. ESTADOS
  const [sliderValue, setSliderValue] = useState(sliderBehavior.initialValue);
  // Al cambiar el paso (visibility 30 min ↔ capas horarias) se conserva la hora, quitando la media hora igual que
  // hace handleModelClick con cqlFecha. Se ajusta en el render para que el thumb no salte a otra hora un frame.
  const [prevStepMinutes, setPrevStepMinutes] = useState(stepMinutes);
  if (prevStepMinutes !== stepMinutes) {
    setPrevStepMinutes(stepMinutes);
    // En D-0 nunca antes de la primera posición libre (dust: 01:00 → 03:00)
    setSliderValue(Math.max(Math.floor((sliderValue * prevStepMinutes) / stepMinutes), sliderBehavior.initialValue));
  }
  const [startDate, setStartDate] = useState(new Date(cqlFechaMin));
  const [isAutoPlay, setIsAutoPlay] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [adjustedDateTime, setAdjustedDateTime] = useState('');

  // null: al montar también manda su posición inicial. El slider se remonta con cada issue/día (key en ForecastMapView)
  // y, empezando con los valores actuales, el efecto no saltaba: el thumb iba a la hora del issue pero cqlFecha se
  // quedaba en la anterior y el ground truth pintaba otra hora que el pronóstico
  const previousIssueTime = useRef(null);
  const previousCqlFechaMin = useRef(null);

  // Tras cambiar el paso se manda la hora de la nueva posición, así cqlFecha coincide con el thumb (hourly 07:00 → dust 06:00)
  const notifiedStepMinutes = useRef(stepMinutes);
  useEffect(() => {
    if (notifiedStepMinutes.current === stepMinutes) return;
    notifiedStepMinutes.current = stepMinutes;
    onChange(addMinutes(new Date(cqlFechaMin), sliderValue * stepMinutes).toISOString());
  }, [stepMinutes, sliderValue, cqlFechaMin, onChange]);

  // Generación de marcas (Cada 4 horas por ejemplo, o todas si son pocas)
  const marks = useMemo(() => Array.from({ length: steps }, (_, i) => {
    // Si steps es 24 (1 hora), mostramos marca cada 2 o 4 horas para no saturar
    // O mostramos todas si steps es pequeño. Aquí asumimos steps=24 aprox.
    const showLabel = (i % Math.max(1, 2 * stepsPerHour) === 0); // dust (3 h): todas
    const markDate = addMinutes(new Date(cqlFechaMin), i * stepMinutes);
    const isBlocked = sliderBehavior.blockedPositions.includes(i);
    return {
      value: i,
      label: showLabel ? format(markDate, 'H') : '',
      blocked: isBlocked
    };
  }), [cqlFechaMin, steps, stepMinutes, stepsPerHour, sliderBehavior.blockedPositions]);
  const handlePlayButtonClick = () => { setIsAutoPlay(!isAutoPlay); setIsPlaying(!isPlaying); };

  const debounce = (func, wait) => {
    let timeout;
    return function (...args) {
      const context = this;
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(context, args), wait);
    };
  };

  const debouncedOnChange = useCallback(debounce((selectedDate) => { onChange(selectedDate.toISOString()); }, 500), [onChange]);

  const handleSliderChange = (event, value) => {
    if (sliderBehavior.blockedPositions.includes(value)) return;

    setSliderValue(value);
    const selectedDate = addMinutes(new Date(cqlFechaMin), value * stepMinutes);
    debouncedOnChange(selectedDate);
    setAdjustedDateTime(format(selectedDate, 'yyyy-MM-dd HH:mm:ss'));
  };

  // 3. EFECTO DE SINCRONIZACIÓN
  useEffect(() => {
    const issueTimeChanged = previousIssueTime.current !== issueTime;
    const cqlFechaMinChanged = previousCqlFechaMin.current !== cqlFechaMin;

    if ((issueTimeChanged || cqlFechaMinChanged) && cqlFechaMin) {
      // 🔥 AL ACTUALIZARSE: Saltamos a la posición inicial calculada (la hora Issue)
      setSliderValue(sliderBehavior.initialValue);
      const initialD = addMinutes(new Date(cqlFechaMin), sliderBehavior.initialValue * stepMinutes);
      setAdjustedDateTime(format(initialD, 'yyyy-MM-dd HH:mm:ss'));
      onChange(initialD.toISOString());

      previousIssueTime.current = issueTime;
      previousCqlFechaMin.current = cqlFechaMin;
    }
  }, [issueTime, cqlFechaMin, sliderBehavior.initialValue, stepMinutes, onChange]);

  // Autoplay
  useEffect(() => {
    if (isAutoPlay) {
      const interval = setInterval(() => {
        setSliderValue((prevValue) => {
          let nextValue = prevValue + 1;
          while (nextValue <= sliderBehavior.maxValue && sliderBehavior.blockedPositions.includes(nextValue)) {
            nextValue++;
          }
          if (nextValue > sliderBehavior.maxValue) {
            nextValue = sliderBehavior.initialValue;
            while (sliderBehavior.blockedPositions.includes(nextValue) && nextValue <= sliderBehavior.maxValue) {
              nextValue++;
            }
          }
          const newDateTime = addMinutes(startDate, nextValue * stepMinutes);
          debouncedOnChange(newDateTime);
          setAdjustedDateTime(format(newDateTime, 'yyyy-MM-dd HH:mm:ss'));
          return nextValue;
        });
      }, 1500);
      return () => clearInterval(interval);
    }
  }, [isAutoPlay, startDate, debouncedOnChange, sliderBehavior, stepMinutes]);

  useEffect(() => { setStartDate(new Date(cqlFechaMin)); }, [cqlFechaMin]);

  // CSS Classes
  const getSliderClasses = () => {
    let classes = "forecast-metric-slider__slider";
    if (sliderBehavior.dayDifference === 0) classes += " forecast-metric-slider--d0";
    else if (sliderBehavior.dayDifference === 2) classes += " forecast-metric-slider--d2";
    return classes;
  };

  // 4. PREPARAR FECHA BASE PARA TOOLTIP
  // Protección defensiva para inicio
  const baseString = cqlFechaMin || issueTime || new Date().toISOString();
  const issueBaseDate = new Date(baseString);
  const isValidDate = !isNaN(issueBaseDate.getTime());
  if (isValidDate) issueBaseDate.setHours(0, 0, 0, 0);

  return (
    <div className="forecast-metric-slider">
      <div className="forecast-metric-slider__container">
        <button
          className={`forecast-metric-slider__button ${isAutoPlay ? 'forecast-metric-slider__button--stop' : 'forecast-metric-slider__button--play'}`}
          onClick={handlePlayButtonClick}
          type="button"
        >
          {isAutoPlay ? <StopCircleRoundedIcon className="forecast-metric-slider__icon" /> : <PlayCircleOutlineRoundedIcon className="forecast-metric-slider__icon" />}
        </button>

        <Box
          className="forecast-metric-slider__wrapper"
          sx={{
            // Calcula el porcentaje de bloqueo inicial basado en la hora Issue
            '--blocked-percentage': sliderBehavior.dayDifference === 0
              ? `${(sliderBehavior.initialValue / (steps - 1)) * 100}%`
              : '0%'
          }}
        >
          <Slider
            value={sliderValue}
            step={1}
            marks={marks}
            min={0}
            max={steps - 1}
            onChange={handleSliderChange}
            valueLabelDisplay="auto"
            // Formato con Año-Mes-Dia Hora:Min UTC
            valueLabelFormat={(value) => {
              if (!isValidDate) return "";
              return format(addMinutes(issueBaseDate, value * stepMinutes), "yyyy-MM-dd HH:mm 'UTC'");
            }}
            className={getSliderClasses()}

            classes={{
              root: 'forecast-metric-slider__root',
              rail: 'forecast-metric-slider__rail',
              track: 'forecast-metric-slider__track',
              thumb: 'forecast-metric-slider__thumb',
              mark: 'forecast-metric-slider__mark',
              markActive: 'forecast-metric-slider__mark--active',
              markLabel: 'forecast-metric-slider__mark-label',
              valueLabel: 'forecast-metric-slider__value-label',
            }}
          />
        </Box>
      </div>
    </div>
  );
};

export default TimeSliderForecastMetric;